import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MoreHorizontal, Paperclip } from 'lucide-react';
import { Badge } from '@/components/ui/badge.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { formatCurrency } from '@/lib/format.js';
import { ApiError, api } from '@/lib/api.js';
import TermPicker, { termMonthsOf, TERM_MAX_MONTHS } from '@/components/TermPicker.jsx';
import { fromApplicationLines } from '@/lib/quoteCompleteness.js';
import { listDealMarkets, quickLogQuote, setMarketState, setMarketClearing, recordMarketReferral } from '@/services/markets.service.js';
import { getQuotesForDealPanel, getQuotePrefill, getQuoteDocs, attachQuoteSourcePdf, patchQuote, forkQuote, clearQuoteReferral } from '@/services/quotes.service.js';
import { sendTemplateForDeal } from '@/services/comms.service.js';
import { DeclineDialog, ageDays } from './MarketLadderPanel.jsx';
import { sentAt, receivedAt, declinedAt, dateWithAge, shortDate } from '@/lib/marketDates.js';
import { declineLabels } from '@/lib/declineReasons.js';
import { formatLiability } from '@/lib/liability.js';
import QuoteWizardSheet from '@/components/sheets/QuoteWizardSheet.jsx';
import GlobalQuoteAction from './GlobalQuoteAction.jsx';
import IatQuoteAction from './IatQuoteAction.jsx';
import CompareQuotesSheet from '@/components/sheets/CompareQuotesSheet.jsx';
import { useApiCarrierReadiness } from '@/lib/api-carriers.js';
import SubmissionRecordDrawer from './SubmissionRecordDrawer.jsx';
import PhantomMarketRow from './PhantomMarketRow.jsx';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu.jsx';

// True while a desktop file is being dragged (not a text/element drag).
function isFileDrag(e) {
  const t = e.dataTransfer?.types;
  return t && Array.from(t).includes('Files');
}

function firstDroppedPdf(e) {
  const f = e.dataTransfer?.files?.[0] || null;
  return f;
}

// Cockpit V-B4 Ship 2: the markets work surface. Replaces the plain ladder in
// the ?focus=markets pane. Dollars lead — a quoted row shows its premium large
// and green with a Present action; a sent row carries an inline quick-log
// (type a premium, Log) plus a required-reason decline; a declined row is muted
// with its reason inline. Header offers "View submission intake" and the
// existing "Log a submission" door.

function agoLabel(iso) {
  const d = ageDays(iso);
  if (d == null) return '';
  if (d === 0) return 'today';
  return `${d}d ago`;
}

// Terms one-liner beside a quoted carrier: what they quoted, muted.
//
// LIABILITY LEADS, and it was not here at all until prompt 100. The line was
// coverage type, hull and effective date — so a $1M/$100K quote showed its hull
// and never its limits, on the one surface David works the shopping from. The
// numbers were in the record the whole time (Milam's Mach 2 options carry
// 1,000,000 and 100,000 right now); nothing rendered them, which is why it read
// as "there's no way to set the liability sublimit".
//
// It leads because a per-passenger sublimit is the difference between two
// policies that are not the same coverage and do not price the same. A hull
// figure beside a bare premium is the least of what he needs to compare.
function quoteTerms(q) {
  if (!q) return '';
  const parts = [];
  const liab = formatLiability(q);
  if (liab) parts.push(liab);
  if (q.coverage_type) parts.push(q.coverage_type);
  if (q.hull_value) parts.push(`${formatCurrency(q.hull_value)} hull`);
  const eff = typeof q.proposed_effective === 'string' ? q.proposed_effective.slice(0, 10) : '';
  if (eff) parts.push(`eff ${eff}`);
  return parts.join(' · ');
}

// A Global referral rides state 'working' with the reason in the blurb behind
// this prefix. There is no 'referred' state in the market ladder and there
// should not be: a referral IS with the carrier, in progress. Keep this literal
// in step with routes/global.routes.js REFERRED_PREFIX.
const REFERRED_PREFIX = 'Referred:';

// The caveats that used to be a toast description dump. A list of things to
// check before presenting is reference material, not a notification: it belongs
// where the outcome lives, collapsed until the broker wants it.
// The row's occasionals. Every state gets one, including referred and declined:
// a submission that came back with no terms is exactly the one a broker wants to
// read back. Broker-only, like everything it opens.
// A ROW CAN BE `quoted` AND HOLD NO QUOTE, and that is the state every control
// on this row assumed away (prompt 96 item 1). recordQuoted moves the market the
// moment a carrier's answer is observed; the quotation is a separate write that
// can fail, be skipped, or simply not have happened yet — the Global row on
// N5461P sat like this with the blurb "Premium to be logged by the broker."
//
// ONE PREDICATE, read by every control that used to guess. Each of them had its
// own way of asking and each of them answered "there is nothing here" with a
// refusal instead of a door.
function quoteIdFor(market, quote) {
  const ids = Array.isArray(market?.quote_ids) ? market.quote_ids : [];
  return (ids.length ? ids[ids.length - 1] : null) || quote?.id || null;
}
function needsQuote(market, quote) {
  return !quoteIdFor(market, quote);
}

function RowMenu({ market, quote, onSubmissionRecord, onFork, onUnquote, onClearing, onAddOption }) {
    // The row's linked capture. Every state can have one, including referred
    // and declined, which are the records most worth reading back.
    //
    // quote_ids is written ONLY by quick-log, so a carrier quoted before this
    // market row existed has an empty array and lost its whole menu — the two
    // actions most worth having on exactly that row. The carrier-matched quote
    // is the same one the premium above renders, so it is the honest fallback.
    // 10 of 31 market rows on file have an empty quote_ids.
    const quoteId = quoteIdFor(market, quote);
    // IT USED TO RETURN null HERE, so the menu vanished from precisely the rows
    // that need repair. A quote-less row still has one thing worth doing to it:
    // saying it was never quoted. Everything else in the menu needs a quotation
    // and is omitted rather than offered and then refused.
    if (!quoteId && !onUnquote && !onClearing) return null;
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    aria-label="More actions"
                    className="shrink-0 rounded-[5px] border border-border px-1.5 py-1 text-text-muted hover:border-border-strong hover:bg-surface-strong hover:text-text"
                >
                    <MoreHorizontal size={14} strokeWidth={1.7} />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {quoteId ? (
                    <DropdownMenuItem onSelect={() => onSubmissionRecord(quoteId)}>
                        View submission record
                    </DropdownMenuItem>
                ) : null}
                {/* THE OPTION DOOR (prompt 103 item 2). This row offered only
                    "Fork as an alternate", so David forked Mach 2 to record
                    $2M/$200K — and his own ruling (OPTIONS-MODEL-SPEC,
                    2026-08-01) says a limits change on the same fleet and pilot
                    set is an OPTION, not an alternate. He used the fork because
                    it was the only door here. This is the right one, and it goes
                    ABOVE the fork so the common case is the first thing read.
                    It opens the wizard, which is where options are authored —
                    deliberately not a second authoring path. */}
                {quoteId ? (
                    <DropdownMenuItem onSelect={() => onAddOption?.(quoteId)}>
                        Add an option (same fleet, different terms)
                    </DropdownMenuItem>
                ) : null}
                {/* An ALTERNATE, not a revision: a CHANGED fleet or pilot set.
                    Both rows stay live and the original is untouched, which is
                    why the verb is his to pick rather than something the system
                    infers from the terms. */}
                {quoteId ? (
                    <DropdownMenuItem onSelect={() => onFork?.(quoteId)}>
                        Fork as an alternate (different fleet or pilots)
                    </DropdownMenuItem>
                ) : null}
                {/* The repair for a row marked quoted that never had a quote.
                    It moves the market's own state and writes nothing else — it
                    is not a decline, and it does not claim the carrier said
                    anything. */}
                {onUnquote ? (
                    <DropdownMenuItem onSelect={() => onUnquote(market)}>
                        Not quoted after all, back to sent
                    </DropdownMenuItem>
                ) : null}
                {/* THE REPAIR FOR THE EIGHT (prompt 98 item 3). `clearing` had
                    one writer, at the moment the row is born, so every clearance
                    logged by hand before the phantom row's checkbox existed
                    filed as a submission and the nudge clock has been chasing
                    him to follow up on markets nobody asked anything of. */}
                {onClearing ? (
                    <DropdownMenuItem onSelect={() => onClearing(market, market.clearing !== true)}>
                        {market.clearing === true
                            ? 'This was a submission after all'
                            : 'This was a clearance, not a submission'}
                    </DropdownMenuItem>
                ) : null}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

// WHAT THIS MARKET ACTUALLY OFFERED (prompt 103 item 2).
//
// One row per market — a market is a place David sent it, and that does not
// change because they quoted two ways. But the row must never read as the whole
// answer when it is not: collapsed it says how many offers it holds, and one
// click lists every one with its limits and its own price.
//
// An OFFER here is a quote row OR an option under one. Both grains, because both
// exist: David's Mach 2 pair is two quotes (a fork), and a sibling option is one
// quote with two rows under it. The ladder should not care which the broker
// happened to create — see the note on the option door below.
function offerRows(offers) {
    const out = [];
    for (const q of offers || []) {
        const opts = Array.isArray(q.options) ? q.options.filter((o) => !o.excluded) : [];
        if (opts.length <= 1) {
            out.push({
                key: q.id,
                quoteId: q.id,
                label: q.carrier_quote_number || q.option_label || '',
                premium: q.premium,
                terms: q,
                selected: opts[0]?.selected === true,
            });
            continue;
        }
        for (const o of opts) {
            out.push({
                key: `${q.id}:${o.id}`,
                quoteId: q.id,
                label: o.option_label || (o.term_variant_of ? `${o.term_months}-month` : ''),
                premium: o.premium,
                terms: o,
                selected: o.selected === true,
            });
        }
    }
    return out;
}

function OffersStrip({ offers, onOpenQuote }) {
    const [open, setOpen] = useState(false);
    const rows = offerRows(offers);
    // One offer is the ordinary case and needs no disclosure at all: the row's
    // own premium already IS the whole answer.
    if (rows.length <= 1) return null;
    return (
        <div className="mt-0.5">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="text-[11px] font-medium text-warning underline decoration-dotted underline-offset-2 hover:text-warning/80"
            >
                {open ? 'Hide the offers' : `${rows.length} offers from this market`}
            </button>
            {open ? (
                <ul className="mt-1 space-y-0.5 rounded border border-border bg-bg/40 px-2.5 py-1.5">
                    {rows.map((r) => (
                        <li key={r.key} className="flex flex-wrap items-baseline gap-x-2 text-[11px] leading-snug">
                            {/* NO PLACEHOLDER WORD. This printed the literal
                                'Option' under two rows that were otherwise
                                identical, which is what David was looking at:
                                "all i see is vaguecity". Where a row has no
                                label the terms beside it ARE the answer, and the
                                row stays clickable through them. */}
                            <button
                                type="button"
                                onClick={() => onOpenQuote?.(r.quoteId)}
                                className="text-text-muted underline decoration-dotted underline-offset-2 hover:text-text"
                            >
                                {r.label || formatLiability(r.terms) || 'Open'}
                            </button>
                            {r.label && formatLiability(r.terms)
                                ? <span className="text-text-dim">{formatLiability(r.terms)}</span> : null}
                            {r.terms?.hull_value ? <span className="text-text-dim">{formatCurrency(r.terms.hull_value)} hull</span> : null}
                            {r.premium != null ? (
                                <span className="ml-auto font-semibold text-success">{formatCurrency(Number(r.premium))}</span>
                            ) : <span className="ml-auto text-text-dim">no premium</span>}
                            {r.selected ? <span className="text-[10px] uppercase tracking-wide text-accent">selected</span> : null}
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

function RowNotes({ notes }) {
    const [open, setOpen] = useState(false);
    if (!notes || notes.length === 0) return null;
    return (
        <div className="mt-0.5">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="text-[11px] text-text-dim underline decoration-dotted underline-offset-2 hover:text-text-muted"
            >
                {open ? 'Hide' : `${notes.length} to check`}
            </button>
            {open ? (
                <ul className="mt-1 space-y-0.5">
                    {notes.map((n, i) => (
                        <li key={i} className="text-[11px] leading-snug text-text-muted">{n}</li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

// THE FLAG FIRST, the string second (wave 3 item 3). quotes.referred is a real
// boolean with existing writers and readers; the blurb prefix is how Global's
// API happened to record a referral before there was a broker control, and it is
// kept ONLY so rows written that way keep rendering. tests/market-referred.test.js
// fails if the two REFERRED_PREFIX constants ever drift apart.
//
// STATE-GUARDED (187 §7 step 2). The flag has exactly one writer that sets it
// true and, until this prompt, none that set it back, so a market David moved
// to `declined` kept rendering amber "Referred, no terms yet" forever. Three of
// the four IAT rows in 187 §3 were doing exactly that, measured before the
// change. An annotation may change how a row READS. It may never outrank the
// row's own outcome.
const TERMINAL_MARKET_STATES = new Set(['declined', 'blocked']);
const isReferred = (m, quote) => !TERMINAL_MARKET_STATES.has(String(m?.state || ''))
  && (quote?.referred === true
    || (m.state === 'working' && String(m.blurb || '').startsWith(REFERRED_PREFIX)));

// Did the ROW'S OWN RECORD say a referral happened? Read from history, not from
// an annotation nobody clears (187 addendum §2). This is what lets a terminal
// row say "declined, referred first" without the flag outranking the outcome.
const referredInHistory = (m) => (Array.isArray(m?.state_history) ? m.state_history : [])
  .some((h) => h && h.referral === true);

// blurb = outcome line, then one caveat per line. The caveats are the warnings
// that used to be dumped into a toast description.
function blurbParts(market) {
    const lines = String(market.blurb || '').split('\n').map(l => l.trim()).filter(Boolean);
    return { headline: lines[0] || '', notes: lines.slice(1) };
}

// WHEN THE CARRIER DECLINED, not when the row was last touched. This aged off
// `updated_at`, so editing a decline note moved the date the carrier appeared to
// have declined (wave 3 item 1). state_history has carried the real transition
// timestamp since the table was built. A declined row with no such entry says
// "date unknown" rather than borrowing one.
function declineLine(market) {
  const when = declinedAt(market);
  const stamp = when ? dateWithAge(when) : 'date unknown';
  const r = market.declination_reason;
  // Reasons are PLURAL. declineLabels also accepts a bare scalar so rows written
  // before the migration still render rather than dropping to "declined <date>".
  const labels = declineLabels(r);
  if (!labels.length) return `declined ${stamp}`;
  return `declined ${stamp}: ${labels.join(', ')}${r.detail ? `, ${r.detail}` : ''}`;
}

// The submission's own date line, absolute first. "3d ago" is doing real work
// (he reads recency at a glance) so it stays alongside rather than instead.
// No date at all is stated outright: a row that was never marked sent must not
// silently render as one sent whenever the row happened to be created.
function SentLine({ market, quote }) {
  const when = sentAt(market, quote);
  return (
    <span>{when ? `sent ${dateWithAge(when)}` : 'no sent date recorded'}</span>
  );
}

// ONCE AN ANSWER EXISTS, THE ANSWER'S DATE IS THE HEADLINE (prompt 67 item 2.3).
// A quoted row rendered SentLine, so Global came back with $3,885 and the line
// under it still read "sent Aug 4 (today)". The send date is the wrong lead on a
// row whose news is the reply.
//
// Both dates ride, because the gap between them is the fact David actually reads
// off this line: how long that carrier took. The send keeps its bare date and the
// arrival carries the "(3d ago)" age, so the recency at a glance describes the
// most recent event rather than the oldest one.
//
// Falls back to SentLine when no arrival date can be established. It never
// borrows the sent date and relabels it "received" — a row that cannot say when
// the answer landed says only when it went out.
function AnsweredLine({ market, quote }) {
  const got = receivedAt(market, quote);
  if (!got) return <SentLine market={market} quote={quote} />;
  const out = sentAt(market, quote);
  if (!out) return <span>{`received ${dateWithAge(got)}`}</span>;
  return <span>{`sent ${shortDate(out)} → received ${dateWithAge(got)}`}</span>;
}

// WHAT WAS SENT, on the row (wave 3 item 2). David: "I should see the
// submission and date and the submission payload right there immediately. What
// pilots, what aircraft".
//
// Collapsed to one line because every row gets one and a deal has many; one
// click opens it in place. Deliberately NOT in the row menu, NOT a drawer and
// NOT a route change: "right there immediately" rules all three out.
//
// The summary is built server-side from the FROZEN snapshot and is an allowlist
// (submission-markets.service.js summariseSnapshot). Nothing here re-derives
// anything, so a row with no capture says so and stops.
function SubmissionPayload({ summary }) {
  const [open, setOpen] = useState(false);
  if (!summary) return null;
  if (!summary.captured) {
    // Manual rows have no snapshot, and that IS the answer. A live re-derive
    // would show today's roster as though it were the one submitted.
    return <p className="text-[11px] text-text-dim">no payload captured</p>;
  }
  const pilots = Array.isArray(summary.pilots) ? summary.pilots : [];
  const aircraft = Array.isArray(summary.aircraft) ? summary.aircraft : [];
  const cov = summary.coverage || {};
  const money = (v) => (v == null || v === '' ? null : (typeof v === 'number' ? formatCurrency(v) : String(v)));
  const limits = [
    money(cov.liability_per_occurrence),
    money(cov.liability_per_passenger),
    money(cov.medical_payments),
  ].filter(Boolean).join(' / ');
  return (
    <div className="mt-0.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[11px] text-text-dim underline decoration-dotted underline-offset-2 hover:text-text-muted"
      >
        {open ? 'Hide what was sent' : `sent ${aircraft.length} aircraft, ${pilots.length} pilot${pilots.length === 1 ? '' : 's'}${limits ? `, ${limits}` : ''}`}
      </button>
      {open ? (
        <div className="mt-1 space-y-0.5 rounded border border-border bg-bg/40 px-2.5 py-1.5">
          {aircraft.length ? (
            <p className="text-[11px] leading-snug text-text-muted">
              <span className="text-text-dim">Aircraft: </span>
              {aircraft.map((a) => [a.tail_number, a.make_model].filter(Boolean).join(' ')).join(', ')}
            </p>
          ) : null}
          {pilots.length ? (
            <p className="text-[11px] leading-snug text-text-muted">
              <span className="text-text-dim">Pilots: </span>{pilots.join(', ')}
            </p>
          ) : null}
          {limits ? (
            <p className="text-[11px] leading-snug text-text-muted">
              <span className="text-text-dim">Requested: </span>{limits}
              {cov.effective_date ? ` eff ${String(cov.effective_date).slice(0, 10)}` : ''}
            </p>
          ) : null}
          {cov.deductible_in_motion || cov.deductible_not_in_motion ? (
            <p className="text-[11px] leading-snug text-text-muted">
              <span className="text-text-dim">Deductibles: </span>
              {[cov.deductible_in_motion, cov.deductible_not_in_motion].filter(Boolean).join(' / ')}
            </p>
          ) : null}
          {summary.frozen_at ? (
            <p className="text-[10.5px] text-text-dim">frozen {String(summary.frozen_at).slice(0, 10)}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function toNum(v) {
  const n = Number(String(v ?? '').replace(/[^\d.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

// THE ONE RULE for the map the rows read premiums out of: lowest premium per
// resolved carrier. Both the refetch and the instant paint after a quick-log go
// through here (prompt 67 item 2.2), because two copies of "which quote speaks
// for this carrier" would agree on the common case and disagree on the day a
// carrier has two live quotes — showing one number on log and a different one
// after the refetch.
//
// KEYED ON THE RESOLVED CARRIER, NOT ON A DISPLAY NAME. This used to match
// carriers.name.toLowerCase().trim() against `q.underwriter || q.carrier`, which
// is three strings any two of which can drift: applyCarrierIdentity rewrites
// `carrier` to the resolver's canonical form, and `underwriter` is a person's
// name on plenty of rows. A miss looked identical to the premium having
// vanished. carrier_ref_id is the Supabase FK on both sides.
//
// A quote whose carrier never resolved has no key. It is skipped rather than
// bucketed under '', which would hand one arbitrary premium to whichever
// unresolved row asked first. Pure: returns the map unchanged when nothing wins.
// EVERY OFFER A CARRIER MADE, keyed the same way (prompt 103 item 2).
//
// mergeQuoteIntoMap below keeps the LOWEST premium per carrier, which is right
// for the one number a collapsed row leads with and wrong as the whole answer:
// David forked Mach 2 into a second quote for $2M/$200K and the ladder showed
// one row at $8,932. Once the second is priced higher it would show $8,932
// forever and never mention the other. A row stating the lower of two premiums
// for a market that quoted both is stating something false.
//
// So the offers are collected too, and the row says how many it holds.
//
// AND IT DEDUPES ON THE CARRIER'S OWN IDENTIFIER (189 §3). Measured 2026-08-19:
// 21 deal+carrier groups render "lowest of N" over a SINGLE premium, because a
// capture row and a cockpit row for one offer were both being counted. "Lowest
// of 2" on one quote counted twice is a false statement about what a carrier
// offered.
//
// THE ONLY MATCH IS carrier_id + a non-null quote number -- the carrier's own
// identifier for its own offer. Never premium, never similarity, never
// anything fuzzy: two real offers at the same price must still read as two.
// A quote with no number is always kept, because absence is not a match.
//
// DISPLAY ONLY. Nothing is written, nothing is merged in the store, and
// mergeQuoteIntoMap is deliberately untouched -- which quote LEADS the row is a
// different question and not this one.
function collectOffers(map, q) {
  const key = q && q.carrier_ref_id != null ? String(q.carrier_ref_id) : '';
  if (!key) return map;
  const cur = map[key] || [];
  const qn = q.carrier_quote_number ?? q.quote_number ?? null;
  const num = qn == null ? '' : String(qn).trim();
  if (num && cur.some((o) => String(o.carrier_quote_number ?? o.quote_number ?? '').trim() === num)) {
    return map;
  }
  return { ...map, [key]: [...cur, q] };
}

function mergeQuoteIntoMap(map, q) {
  const key = q && q.carrier_ref_id != null ? String(q.carrier_ref_id) : '';
  if (!key) return map;
  const p = q.premium != null ? Number(q.premium) : null;
  const cur = map[key];
  if (!cur || (p != null && !Number.isNaN(p) && (cur.premium == null || p < cur.premium))) {
    return { ...map, [key]: q };
  }
  return map;
}

// The quick-log's second line (hoisted: no-unstable-nested-components): the
// term as David confirms it (decision 14), the "I&R, OPW only" box (B14, one
// writer: mapBodyToQuote; nothing is guessed from aircraft use), and what the
// log takes from the application, marked so he can see what he did not type
// (B6, the server's own list).
function QuickLogExtras({ term, onTerm, irOpwOnly, onIrOpwOnly, seeded, disabled }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <TermPicker value={term} onChange={onTerm} disabled={disabled} />
        <label className="flex items-center gap-1.5 text-[11.5px] text-text-muted">
          <input type="checkbox" checked={irOpwOnly === true} disabled={disabled} data-ir-opw-only
            onChange={(e) => onIrOpwOnly(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          I&amp;R, OPW only
        </label>
      </span>
      {seeded === 'unread' ? (
        <span className="text-[11px] text-warning" data-from-application>Could not read what this log takes from the application.</span>
      ) : Array.isArray(seeded) && seeded.length ? (
        <span className="text-[11px] text-text-dim" data-from-application>
          <span className="rounded border border-border px-1 py-px text-[10px] uppercase tracking-wide">from the application</span>{' '}
          {seeded.join(' · ')}
        </span>
      ) : null}
    </div>
  );
}

// Inline quick-log (Ship 2.2 + A4). Single-aircraft deal: one premium field.
// Multi-aircraft deal: a "Log quote…" toggle expands one premium field per tail
// with a live computed total (pre-tax). The mint prefills AS APPLIED FOR
// server-side; here we only carry the premium(s).
export function QuickLog({ dealId, market, aircraft, pendingFile, onPendingConsumed, onChanged, onQuoteLogged, onClearReferred }) {
  const multi = Array.isArray(aircraft) && aircraft.length > 1;
  const [open, setOpen] = useState(false);
  const [single, setSingle] = useState('');
  const [byTail, setByTail] = useState({});
  const [logging, setLogging] = useState(false);
  // What was last sent, as a payload string, and whether a send is in flight.
  // Refs rather than state because both are read inside submit() in the same
  // tick a blur and a click can both fire in, and a re-render is not wanted.
  const committedRef = useRef('');
  const submittingRef = useRef(false);
  // THE TERM, 12 PRE-PICKED (decision 14), and whether he picked it himself.
  const [term, setTerm] = useState('12');
  const [termTouched, setTermTouched] = useState(false);
  // B14: null until he touches the box, so an untouched box sends nothing.
  const [irOpwOnly, setIrOpwOnly] = useState(null);
  // B6: what this log would take from the application, read once the form
  // shows (the compose read writes nothing). 'unread' on a failed read.
  const [seeded, setSeeded] = useState(null);
  const composeAskedRef = useRef(false);
  const pickTerm = (v) => { setTerm(v); setTermTouched(true); };
  function resetExtras() { setTerm('12'); setTermTouched(false); setIrOpwOnly(null); }
  const showing = multi ? open : !!single.trim();
  useEffect(() => {
    if (!showing || composeAskedRef.current) return;
    composeAskedRef.current = true;
    api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/markets/${encodeURIComponent(market.id)}/quick-log`)
      .then((r) => setSeeded(fromApplicationLines(r?.from_application, r?.body, aircraft)))
      .catch(() => setSeeded('unread'));
  }, [showing, dealId, market.id, aircraft]);

  // A PDF dropped on this sent row opens the quick-log (drop implies a quote
  // arrived); the file attaches to the quote the moment the broker logs it.
  useEffect(() => {
    if (pendingFile && multi) setOpen(true);
  }, [pendingFile, multi]);

  const total = multi
    ? aircraft.reduce((s, a) => s + toNum(byTail[a.tail_number_id]), 0)
    : toNum(single);

  // The typed numbers as the API takes them, or null when nothing is typed.
  // The term rides as picked; the I&R box only once he touched it (B14).
  function buildPayload() {
    const extras = { term_months: termMonthsOf(term), ...(typeof irOpwOnly === 'boolean' ? { ir_opw_only: irOpwOnly } : {}) };
    if (multi) {
      const rows = aircraft
        .map((a) => ({ tail_number_id: a.tail_number_id, premium: toNum(byTail[a.tail_number_id]) }))
        .filter((r) => r.premium > 0);
      return rows.length ? { aircraft_premiums: rows, ...extras } : null;
    }
    const p = toNum(single);
    if (!p) return null;
    if (Array.isArray(aircraft) && aircraft.length === 1) {
      return { aircraft_premiums: [{ tail_number_id: aircraft[0].tail_number_id, premium: p }], ...extras };
    }
    return { premium: p, ...extras };
  }

  // Focus left the whole quick-log, so commit what is in it. Scoped to the FORM
  // and not to the input on purpose: a fleet deal renders one box per tail, and
  // committing on each box's own blur would mint the quote off the first tail
  // the moment he tabbed to the second, unmounting the rest of the fleet with
  // it. Staying inside the form (the next tail, the Log button, cancel) is not
  // a departure. `quiet` because clicking past an empty row is not an error.
  //
  // A press INSIDE the form is not a departure either, even when the browser
  // does not focus what was pressed (Safari and macOS Firefox leave a clicked
  // button or checkbox unfocused, so the blur arrives with relatedTarget null).
  // Without this, picking 6 months or ticking the I&R box logged the pre-picked
  // 12 and an untouched box before the click landed. The flag lives one task:
  // the blur a mousedown causes is dispatched before the timeout runs.
  const pressedInsideRef = useRef(false);
  function pressInside() {
    pressedInsideRef.current = true;
    setTimeout(() => { pressedInsideRef.current = false; }, 0);
  }
  function commitOnBlur(e) {
    if (e.currentTarget.contains(e.relatedTarget) || pressedInsideRef.current) return;
    submit({ quiet: true });
  }

  // The one send. `intent` is absent on the first try (the server decides
  // whether a duplicate exists) and 'update' | 'new' when the broker has
  // answered the 409. Both paths land the dropped PDF and repaint identically,
  // which is why they are one function and not two.
  async function sendQuickLog(payload, intent) {
    const r = await quickLogQuote(dealId, market.id, intent ? { ...payload, intent } : payload);
    // BEFORE the PDF attach and before the refetch. The premium is on the row by
    // the time the toast reads, which is the whole of item 2.2. `r.quote` is the
    // server's own panel row; when the server could not build one the number
    // simply waits for the refetch, exactly as it did before.
    if (r?.quote) onQuoteLogged?.(r.quote);
    // Attach the dropped PDF (if any) to the freshly minted quote.
    if (pendingFile && r?.quote_id) {
      try { await attachQuoteSourcePdf(dealId, r.quote_id, pendingFile); }
      catch (aErr) { toast.error(`Quote logged, but the PDF did not attach: ${aErr.message}`); }
    }
    // B6: what the log took from the application, in the server's list.
    const seededNow = fromApplicationLines(r?.from_application, null, aircraft);
    toast.success(
      intent === 'update'
        ? `Updated ${market.carrier_name || 'carrier'} quote`
        : `Logged ${market.carrier_name || 'carrier'} quote`,
      seededNow.length ? { description: `From the application: ${seededNow.join(', ')}` } : undefined
    );
    setSingle(''); setByTail({}); setOpen(false); resetExtras();
    onPendingConsumed?.();
    onChanged?.();
  }

  // THE 409 IS A QUESTION, SO IT GETS THE TWO ANSWERS. The server has always
  // returned the blocking quote and accepted intent 'update' | 'new'; this
  // toast used to drop that body on the floor and leave the broker with a
  // sentence offering two verbs and no way to say either. He then cannot even
  // SEE the quote he is being blocked by, because it predates the market row.
  //
  // No auto-dismiss: a choice that overwrites a live quote must not expire
  // while he is reading it.
  //
  // AN UPDATE NEVER OVERWRITES A CARRIER'S STATED TERM WITH THE PRE-PICK (S5).
  // Unless he picked a term himself, an update to a quote that states one sends
  // no term, so the stored one stands; the toast names it.
  function askIntent(body, payload) {
    const ex = body?.existing_quote || {};
    const when = ex.created_at ? dateWithAge(ex.created_at) : null;
    const prem = ex.total_premium != null ? formatCurrency(Number(ex.total_premium)) : 'no premium on file';
    const keepsTerm = !termTouched && termMonthsOf(ex.term_months) != null;
    const retry = async (intent) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setLogging(true);
      try {
        const { term_months: _pre, ...withoutTerm } = payload;
        await sendQuickLog(intent === 'update' && keepsTerm ? withoutTerm : payload, intent);
        // The answered payload IS committed now. Without this a stray blur on
        // the (already cleared) form could re-send the same numbers.
        committedRef.current = JSON.stringify(payload);
        // TYPING TERMS INTO A REFERRED QUOTE IS THE MOMENT TO ASK. N5997P,
        // 2026-09-21: the firmed IAT figures went in through this exact path
        // and the referred flag outlived them, because nothing here could see
        // it. The question is asked, never assumed: a revised referral looks
        // identical to a firmed rate from this side. Same attributed writer as
        // the row's own "Terms confirmed" control.
        if (intent === 'update' && ex.referred === true && ex.id) {
          toast.info(`${ex.carrier || market.carrier_name || 'This carrier'} is still marked referred. Are these the confirmed terms?`, {
            duration: Infinity,
            action: { label: 'Terms confirmed', onClick: () => onClearReferred?.(ex.id, 'firmed') },
            cancel: { label: 'Still referred' },
          });
        }
      } catch (err) {
        committedRef.current = '';
        const b = err instanceof ApiError ? err.body : null;
        toast.error(b?.error || err.message || 'Quick-log failed');
      } finally {
        submittingRef.current = false;
        setLogging(false);
      }
    };
    toast.error(body?.error || 'This carrier already has a live quote on this deal.', {
      duration: Infinity,
      description: `Existing: ${prem}${when ? ` · logged ${when}` : ''}${ex.status ? ` · ${ex.status}` : ''}${keepsTerm ? ` · Update keeps its ${ex.term_months}-month term` : ''}`,
      action: { label: 'Update it', onClick: () => retry('update') },
      cancel: { label: 'Log as new', onClick: () => retry('new') },
    });
  }

  async function submit({ quiet = false } = {}) {
    // A blur fires before the click that caused it, so Log-after-typing calls
    // this twice. The first call wins and the second finds a send in flight.
    if (submittingRef.current) return;
    // The term is sent as he confirmed it (decision 14), so one that is not a
    // whole number of months stops the log rather than sending something else.
    if (termMonthsOf(term) == null) {
      if (!quiet) toast.error(`Pick the term: 12 months, 6 months, or Other with 1 to ${TERM_MAX_MONTHS} months.`);
      return;
    }
    const payload = buildPayload();
    if (!payload) {
      if (!quiet) toast.error(multi ? 'Type at least one premium' : 'Type a premium first');
      return;
    }
    // Nothing changed since the last send. Tabbing back and forth across a
    // committed form must not mint a second quote for the same number.
    const key = JSON.stringify(payload);
    if (key === committedRef.current) return;
    committedRef.current = key;
    submittingRef.current = true;
    setLogging(true);
    try {
      await sendQuickLog(payload);
    } catch (err) {
      // Forget the committed key, or the retry is a no-op and the number he
      // typed is gone for good, which is the failure this whole item is about.
      committedRef.current = '';
      const body = err instanceof ApiError ? err.body : null;
      if (body?.code === 'QUOTE_EXISTS') askIntent(body, payload);
      else toast.error(body?.error || err.message || 'Quick-log failed');
    } finally {
      submittingRef.current = false;
      setLogging(false);
    }
  }

  if (multi) {
    if (!open) {
      return (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-[5px] border border-border px-2.5 py-1 text-[12px] hover:border-border-strong hover:bg-surface-strong"
        >
          Log quote&hellip;
        </button>
      );
    }
    return (
      <div className="w-full rounded border border-border bg-bg p-2.5" onBlur={commitOnBlur} onMouseDown={pressInside}>
        <div className="space-y-1.5">
          {aircraft.map((a) => (
            <div key={a.tail_number_id} className="flex items-center justify-between gap-2">
              <span className="text-[11.5px] text-text-muted">{a.tail_display || a.tail_number_id}</span>
              <span className="flex items-center gap-1">
                <span className="text-text-muted">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={byTail[a.tail_number_id] || ''}
                  onChange={(e) => setByTail((m) => ({ ...m, [a.tail_number_id]: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                  placeholder="premium"
                  disabled={logging}
                  className="w-24 rounded border border-border bg-surface px-2 py-1 text-[12px] font-semibold text-success placeholder:font-normal placeholder:text-text-dim focus:border-success focus:outline-none"
                />
              </span>
            </div>
          ))}
        </div>
        <div className="mt-2">
          <QuickLogExtras term={term} onTerm={pickTerm} irOpwOnly={irOpwOnly} onIrOpwOnly={setIrOpwOnly}
            seeded={seeded} aircraft={aircraft} disabled={logging} />
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 border-t border-border pt-2">
          <span className="flex items-center gap-2 text-[11.5px] text-text-muted">
            <span>
              Total <span className="font-semibold text-success">{total > 0 ? formatCurrency(total) : '—'}</span>
              <span className="text-text-dim"> pre-tax</span>
            </span>
            {pendingFile ? (
              <span className="inline-flex items-center gap-1 rounded border border-success/40 bg-success-soft px-2 py-0.5 text-[10.5px] text-success" title={pendingFile.name}>
                <Paperclip size={11} /> attaching
              </span>
            ) : null}
          </span>
          <span className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => { setOpen(false); setByTail({}); resetExtras(); }}
              disabled={logging}
              className="text-[11px] text-text-dim hover:text-text-muted"
            >
              cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={logging}
              className="rounded-[5px] border border-accent bg-accent px-2.5 py-1 text-[12px] font-medium text-white hover:brightness-110 disabled:opacity-50"
            >
              {logging ? '…' : 'Log'}
            </button>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1" onBlur={commitOnBlur} onMouseDown={pressInside}>
    <div className="flex items-center gap-1.5">
      {pendingFile ? (
        <span className="inline-flex items-center gap-1 rounded border border-success/40 bg-success-soft px-2 py-0.5 text-[10.5px] text-success" title={pendingFile.name}>
          <Paperclip size={11} /> {pendingFile.name.length > 18 ? `${pendingFile.name.slice(0, 16)}…` : pendingFile.name}
        </span>
      ) : null}
      <span className="text-text-muted">$</span>
      <input
        type="text"
        inputMode="decimal"
        value={single}
        onChange={(e) => setSingle(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        placeholder="premium"
        disabled={logging}
        className="w-24 rounded border border-border bg-bg px-2 py-1 text-[12px] font-semibold text-success placeholder:font-normal placeholder:text-text-dim focus:border-success focus:outline-none"
      />
      <button
        type="button"
        onClick={submit}
        disabled={logging}
        className="rounded-[5px] border border-border px-2.5 py-1 text-[12px] hover:border-border-strong hover:bg-surface-strong disabled:opacity-50"
      >
        {logging ? '…' : 'Log'}
      </button>
    </div>
    {single.trim() ? (
      <QuickLogExtras term={term} onTerm={pickTerm} irOpwOnly={irOpwOnly} onIrOpwOnly={setIrOpwOnly}
        seeded={seeded} aircraft={aircraft} disabled={logging} />
    ) : null}
    </div>
  );
}

// One market row, Ship 2 treatment. `quote` is the deal's matching quotation
// (joined by carrier) when the row is quoted; `aircraft` is the deal's tails
// for the per-aircraft quick-log. `hasDoc` marks a quote with a filed PDF.
// Drag-drop (Ship 2.3): the surface owns drag state; the row reflects it.
function MarketWorkRow({
  dealId, apiReady = {}, market, quote, aircraft, hasDoc, pendingFile,
  dragging, dragOver, droppable, onDragOverRow, onDragLeaveRow, onDropRow,
  onPresent, onQuoteDetail, onPendingConsumed, onChanged, onSubmissionRecord,
  onMarkReferred, referring, onFork, onQuoteLogged, onUnquote, onClearing, offers, onAddOption,
  onClearReferred, clearingReferred,
}) {
  const [declineOpen, setDeclineOpen] = useState(false);
  const carrier = market.carrier_name || 'Ad-hoc';
  // A MARKET TRACON DOES NOT LIST says so, rather than rendering as an ordinary
  // row (prompt 93). It has no rules, no contacts, no signal and no nudge
  // threshold, because it has no carriers entry — the broker typed it because he
  // knows a market TRACON does not. The flag is the prompt to carrier admin.
  const unlisted = market.carrier_unlisted === true ? (
    <span
      className="ml-1.5 rounded border border-border px-1 py-px text-[10px] font-normal text-text-dim"
      title="Not in your carrier list, so nothing is known about it: no contacts, no rules, no follow-up threshold. Add it in carrier admin when you want those."
    >
      unlisted
    </span>
  ) : null;

  // ── ONE DECLINE DOOR, REACHABLE FROM EVERY NON-TERMINAL BRANCH ────────────
  //
  // 187 §2: the row renderer forks six ways and `DeclineDialog` was mounted
  // inside ONE of them. A referred row (branch 3), a quoted row (branch 1) and
  // a parked row (branch 2) all returned before reaching it, so David had no
  // way to move Global on N45RW from referred to declined. The server never
  // objected: validateStateChange checks the TARGET state only, there is no
  // transition matrix, and working → declined has always been legal.
  //
  // Defined ONCE here and rendered by reference, rather than pasted into four
  // branches. Four copies of one control is the shape of most of this month's
  // ledger.
  //
  // An annotation may change how a row READS. It may never change what a row
  // can DO. A quoted carrier can still withdraw; a parked row is not an ending;
  // a referral is a note about the answer, not a suspension of the broker's
  // ability to close the row out. Only genuinely terminal rows have no door.
  const terminal = TERMINAL_MARKET_STATES.has(String(market.state || ''));
  const declineLink = terminal ? null : (
    <button
      type="button"
      onClick={() => setDeclineOpen(true)}
      className="text-[11px] text-text-dim hover:text-text-muted hover:underline"
    >
      decline&hellip;
    </button>
  );
  const declineDialog = terminal ? null : (
    <DeclineDialog market={market} open={declineOpen} onOpenChange={setDeclineOpen} onChanged={onChanged} />
  );

  // ── THE REFERRAL, AS A DECORATION ─────────────────────────────────────────
  const referred = isReferred(market, quote);
  const referralReason = (() => {
    const { headline } = blurbParts(market);
    return String(headline).startsWith(REFERRED_PREFIX)
      ? headline.slice(REFERRED_PREFIX.length).trim()
      : '';
  })();
  const referredBadge = referred ? <Badge variant="warning">Referred</Badge> : null;
  // "The carrier firmed it" (187 §5). The one control the present-door refusal
  // has been pointing at for its whole life, and which existed nowhere: the
  // ONLY writer of referred:false in the codebase was SubmissionBuilderPanel,
  // and only as a side effect of marking a quote reviewed. A refusal that names
  // an action with no door spends the trust the next real message needs.
  const firmedControl = (referred && quote?.id) ? (
    <button
      type="button"
      onClick={() => onClearReferred?.(quote.id, 'firmed')}
      disabled={clearingReferred}
      className="text-[11px] text-text-dim hover:text-success hover:underline disabled:opacity-50"
      title="The carrier came back and agreed the rate. Clears the referred flag, attributed to you."
    >
      {clearingReferred ? '…' : 'Terms confirmed'}
    </button>
  ) : null;

  // Drop affordance applied to every row shape.
  const dropProps = droppable ? {
    onDragOver: (e) => { e.preventDefault(); onDragOverRow?.(market.id); },
    onDragLeave: () => onDragLeaveRow?.(market.id),
    onDrop: (e) => { e.preventDefault(); onDropRow?.(market, e); },
  } : {};
  const dropClass = dragOver
    ? 'border-success bg-success-soft'
    : (dragging && droppable ? 'border-dashed border-border-strong' : 'border-transparent');
  const rowClass = `flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded border py-3 px-1 -mx-1 ${dropClass}`;
  const dropHint = dragging && droppable ? (
    <span className="text-[10.5px] font-medium text-success">drop quote PDF here</span>
  ) : null;

  // Quoted — premium leads.
  if (market.state === 'quoted') {
    const quotedNotes = blurbParts(market).notes;
    const prem = quote?.premium != null ? Number(quote.premium) : null;
    // THE ROW THAT SAYS QUOTED AND HOLDS NOTHING (prompt 96 item 1). It reached
    // this branch, which renders Quote Details and Present and neither of them
    // can do anything, and the ONE control that completes it — QuickLog — was
    // rendered only in the fall-through branch below. So the row could reach a
    // state where the only door out of it was unreachable.
    //
    // The server was never the problem: quick-log has no state guard, it checks
    // the market exists, belongs to the deal, has a carrier and a premium above
    // zero, and would have accepted this call all along.
    const awaitingQuote = needsQuote(market, quote);
    return (
      <div className={`${rowClass} border-b-border/60`} {...dropProps}>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-text">
            {carrier}{unlisted}
            {quoteTerms(quote) ? <span className="ml-2 text-[11.5px] font-normal text-text-muted">{quoteTerms(quote)}</span> : null}
            {dropHint ? <span className="ml-2">{dropHint}</span> : null}
          </p>
          <p className="text-[11.5px] text-text-muted"><AnsweredLine market={market} quote={quote} /></p>
          <OffersStrip offers={offers} onOpenQuote={(qid) => onQuoteDetail?.(market, qid)} />
          <SubmissionPayload summary={market.payload_summary} />
          {hasDoc ? (
            <span className="mt-1 inline-flex items-center gap-1 rounded border border-border bg-bg px-2 py-0.5 text-[10.5px] text-text-muted">
              <Paperclip size={11} /> quote on file
            </span>
          ) : null}
          <RowNotes notes={quotedNotes} />
        </div>
        {prem != null && !Number.isNaN(prem) ? (
          <span className="flex shrink-0 flex-col items-end leading-none">
            <span className="text-[17px] font-bold text-success">{formatCurrency(prem)}</span>
            {/* THE PROGRAM ADMIN FEE (prompt 138 item 3). Real and
                carrier-stated: two rows in the whole table carry a non-zero one
                and David holds the document for both.

                WORDED AS ADDITIONAL, NEVER AS INCLUDED, and kept out of every
                sum: the premium above is the carrier's number and the fee is
                something the client pays on top of it. Adding them would restate
                the carrier's figure as ours.

                The server resolves WHICH option's fee this is; nothing here
                picks. estimated_taxes is deliberately absent: it is a derived
                1% on most rows and must not sit under a premium until a value
                can say who stated it.

                PLACEHOLDER COPY, FLAGGED FOR DAVID. */}
            {quote?.admin_fee != null && Number(quote.admin_fee) > 0 ? (
              <span className="mt-0.5 text-[10px] text-text-muted">
                + {formatCurrency(Number(quote.admin_fee))} program fee
              </span>
            ) : null}
            {/* THE COLLAPSED NUMBER IS THE LOWEST, and it says so when there is
                more than one. Silently showing the lower of two is the false
                statement this item exists to stop. */}
            {offerRows(offers).length > 1 ? (
              <span className="mt-0.5 text-[10px] uppercase tracking-wide text-warning">lowest of {offerRows(offers).length}</span>
            ) : null}
          </span>
        ) : null}

        {awaitingQuote ? (
          <>
            {/* The carrier's own live-API door survives the row being marked
                quoted. The header hides it once a market row exists and the
                row-level copy lived only in the fall-through branch, so the one
                carrier with a live integration lost its door at exactly the
                moment the row said an answer had come back. */}
            {apiReady.iat && market.carrier_slug === 'iat' ? (
              <IatQuoteAction dealId={dealId} onLanded={onChanged} label="Re-quote IAT" />
            ) : null}
            {apiReady.global_aerospace && market.carrier_slug === 'global_aerospace' ? (
              <GlobalQuoteAction dealId={dealId} onLanded={onChanged} />
            ) : null}
            <QuickLog
              dealId={dealId}
              market={market}
              aircraft={aircraft}
              pendingFile={pendingFile}
              onPendingConsumed={onPendingConsumed}
              onChanged={onChanged}
              onQuoteLogged={onQuoteLogged}
              onClearReferred={onClearReferred}
            />
          </>
        ) : (
          <>
            {/* One door per quoted row, not two. This opened QuoteDeltaSheet and
                now opens the wizard, which asks everything that sheet asked plus
                the questions it never had (the paper, TRIA, the pilot warranty in
                full). Labelled for the CONTENTS, not the verb: "Work this quote"
                described what David was about to do, which he already knows,
                instead of what is behind the button. David: "I hate 'Work this
                quote'." (prompt 67).
                RENDERED ONLY WHEN THERE IS ONE. A button whose whole answer was
                the toast "No quotation linked to this row yet" is the pattern
                this prompt is about: a surface naming a problem and offering no
                door. The door is the quick-log beside it. */}
            <button
              type="button"
              onClick={() => onQuoteDetail?.(market)}
              className="shrink-0 rounded-[5px] border border-border px-2.5 py-1 text-[12px] hover:border-border-strong hover:bg-surface-strong"
            >
              Quote Details
            </button>
            <button
              type="button"
              onClick={() => onPresent?.(market, quote)}
              className="shrink-0 rounded-[5px] border border-accent bg-accent px-3 py-1 text-[12px] font-medium text-white hover:brightness-110"
            >
              Present&hellip;
            </button>
          </>
        )}
        {/* THE FLAG, ON THE ROW, BEFORE HE REACHES FOR PRESENT (187 §7 step 4).
            13 quotes carry referred=true on rows in state `quoted`, and this
            branch is tested first, so the ladder showed them as ordinary quoted
            rows with a Present button and said nothing. The refusal in
            present-quotes.service is correct and stays — a rate the carrier has
            not agreed to is not a rate to put in front of a client — but it
            arrived at the click, which is the moment David is least able to
            absorb it, and it named a "clear the referred flag" action that had
            no door anywhere. Both halves are fixed here: he sees it first, and
            the control the refusal names is the one sitting beside it. */}
        {referred ? (
          <span className="flex shrink-0 items-center gap-1.5">
            <Badge variant="warning">Referred</Badge>
            {firmedControl}
          </span>
        ) : null}
        {/* A CARRIER THAT QUOTED CAN STILL WITHDRAW. Quoted is not terminal and
            never was; it simply had no door. Same single dialog. */}
        {declineLink}
        <RowMenu
          market={market}
          quote={quote}
          onSubmissionRecord={onSubmissionRecord}
          onFork={onFork}
          onUnquote={awaitingQuote ? onUnquote : null}
          onClearing={onClearing}
          onAddOption={onAddOption}
        />
        {declineDialog}
      </div>
    );
  }

  // PARKED by the roster-change interrupt (David 2026-08-12): the broker
  // chose DECIDE LATER when this quote's aircraft left the deal. Visibly
  // unresolved, unpresentable, numbers untouched — never trapped by a
  // question he could not answer yet, and never invisible either.
  if (quote?.parked_subject_change) {
    const p = quote.parked_subject_change;
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/60 py-3 last:border-0">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text">{carrier}{unlisted}</p>
          <p className="text-[11.5px] text-text-muted">
            Aircraft changed{Array.isArray(p.tails) && p.tails.length ? `: quoted for ${p.tails.join(', ')}, no longer on the deal` : ''}. Retire it, or re-enter for the new aircraft.
          </p>
          <p className="text-[11.5px] text-text-dim"><SentLine market={market} quote={quote} /></p>
        </div>
        <Badge variant="warning">Parked</Badge>
        {/* A PARKED ROW IS NOT AN ENDING (187 §7 step 5). It is the broker
            saying "decide later" about an aircraft change; the carrier can
            still say no in the meantime, and until now there was nowhere to
            put that. */}
        {declineLink}
        {declineDialog}
      </div>
    );
  }

  // BRANCH 3 IS GONE ON PURPOSE (187 §7 step 1). It used to return here with a
  // name, a reason, "no terms yet", an amber badge and RowMenu — and NOTHING
  // that could end the row. Market 334 is state `working`, which is the
  // awaiting-outcome branch's OWN state, but this test sat above it and stole
  // the row's doors. The referral now decorates that branch instead of
  // replacing it: same badge, same reason line, same suppressed premium, plus
  // quick-log, the carrier's API door, decline and RowMenu.
  // Declined / blocked — muted, reason inline, no actions, not droppable.
  if (market.state === 'declined' || market.state === 'blocked') {
    const line = market.state === 'declined'
      ? declineLine(market)
      : `blocked${market.blocked_by ? ` · held by ${market.blocked_by}` : ''}`;
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/60 py-3 text-text-muted last:border-0">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{carrier}{unlisted}</p>
          <p className="text-[11.5px]"><SentLine market={market} quote={quote} /></p>
          <p className="text-[11.5px]">{line}</p>
          {/* "DECLINED, REFERRED FIRST" — read from the row's own record
              (187 addendum §2), never from the annotation. The flag is
              state-guarded out of the badge above, which is right; without
              this the referral would vanish from the diligent-shopping record
              entirely and the history would read sent → working → declined with
              nothing saying Global looked at a 1974 PA-28 and referred it.
              For Global that referral is the ONLY appetite intelligence on
              file: 4 declines, 4 not_stated, every one of them silent. */}
          {referredInHistory(market) ? (
            <p className="text-[11.5px] text-text-dim">referred first</p>
          ) : null}
          <SubmissionPayload summary={market.payload_summary} />
          <RowNotes notes={blurbParts(market).notes} />
        </div>
        <Badge variant="muted">{market.state === 'declined' ? 'Declined' : 'Blocked'}</Badge>
        <RowMenu market={market} quote={quote} onSubmissionRecord={onSubmissionRecord} onFork={onFork} onClearing={onClearing} onAddOption={onAddOption} />
      </div>
    );
  }

  // Sent / acknowledged / working / no_response — awaiting an outcome. Inline
  // quick-log leads; a muted decline link sits alongside.
  //
  // THE CARRIER'S EXISTING QUOTE RENDERS HERE TOO (prompt 13 item 2). It always
  // reached this row — `quote` is keyed on carrier_ref_id and owes nothing to
  // submission_markets.quote_ids — but only the quoted branch ever drew it. So
  // a carrier with a live quote from an earlier round showed as an empty row
  // ready to log, and the quick-log then 409'd naming a quote that appeared
  // nowhere on the screen. Jennings deal 6108943000024677062: three of four
  // markets, permanently un-loggable and blank.
  // NEVER A DOLLAR FIGURE ON A REFERRED ROW: not $0, not "pending". Global
  // returns no number when it refers, and the old branch got this right; it
  // survives the collapse.
  const sentPrem = referred ? null : (quote?.premium != null ? Number(quote.premium) : null);
  return (
    <div className={`${rowClass} border-b-border/60`} {...dropProps}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text">
          {carrier}{unlisted}
          {quoteTerms(quote) ? <span className="ml-2 text-[11.5px] font-normal text-text-muted">{quoteTerms(quote)}</span> : null}
          {dropHint ? <span className="ml-2">{dropHint}</span> : null}
        </p>
        <p className="text-[11.5px] text-text-muted">
          {/* A CLEARANCE IS NOT A SUBMISSION. `clearing` is a boolean on a row
              whose state must still say 'sent' (the CHECK enum has no 'cleared'
              value yet), and this surface never rendered it — only the retiring
              MarketLadderPanel did. So on the screen David actually works, AIM
              and Acceleration read as submissions he is waiting on. Nothing was
              asked of them; the right to quote was reserved. */}
          {referred
            ? (referralReason || 'with the carrier, no terms yet')
            : market.clearing === true
              ? <>cleared, not submitted {agoLabel(market.updated_at)}</>
              : market.state === 'sent'
                ? <SentLine market={market} quote={quote} />
                : <>{market.state.replace(/_/g, ' ')} {agoLabel(market.updated_at)}</>}
          {referred ? '' : (market.channel ? ` via ${market.channel}` : '')}
          {/* Who produced it, when a producer was named on the send. */}
          {referred ? '' : (market.producer_name ? `, ${market.producer_name}` : '')}
        </p>
        {referred ? <p className="text-[11.5px] text-text-dim"><SentLine market={market} quote={quote} /></p> : null}
        <SubmissionPayload summary={market.payload_summary} />
        {referred ? <RowNotes notes={blurbParts(market).notes} /> : null}
      </div>
      {referred ? <span className="shrink-0 text-[12px] italic text-text-dim">no terms yet</span> : null}
      {referredBadge}
      {sentPrem != null && !Number.isNaN(sentPrem) ? (
        <span className="text-[17px] font-bold text-success">{formatCurrency(sentPrem)}</span>
      ) : null}
      {/* The carrier's own live-API door, once it has a rung. Re-fires the
          submission for THIS carrier and nothing else, which is the whole point
          of it living on the row rather than in the ladder header. */}
      {apiReady.iat && market.carrier_slug === 'iat' ? (
        <IatQuoteAction dealId={dealId} onLanded={onChanged} label="Re-quote IAT" />
      ) : null}
      {apiReady.global_aerospace && market.carrier_slug === 'global_aerospace' ? (
        <GlobalQuoteAction dealId={dealId} onLanded={onChanged} />
      ) : null}
      <QuickLog
        dealId={dealId}
        market={market}
        aircraft={aircraft}
        pendingFile={pendingFile}
        onPendingConsumed={onPendingConsumed}
        onChanged={onChanged}
        onQuoteLogged={onQuoteLogged}
        onClearReferred={onClearReferred}
      />
      {/* "Awaiting referral" (wave 3 item 3). GlobalQuoteAction has been
          telling him to "mark it referred before presenting" at an affordance
          that did not exist anywhere on this surface. It writes the QUOTE's
          referred flag when the row has one, and moves the market to working
          either way, which is what a referral is: with the carrier, in
          progress. */}
      {/* AND ITS UNDO. "awaiting referral" was a one-way switch with no
          reverse, not even for a misclick, because nothing anywhere wrote
          referred:false. */}
      {referred ? (
        <button
          type="button"
          onClick={() => onClearReferred?.(quote?.id, 'misclick')}
          disabled={clearingReferred || !quote?.id}
          className="text-[11px] text-text-dim hover:text-text-muted hover:underline disabled:opacity-50"
          title="It was never referred. Clears the flag without claiming the carrier firmed anything."
        >
          {clearingReferred ? '…' : 'not referred after all'}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onMarkReferred?.(market)}
          disabled={referring}
          className="text-[11px] text-text-dim hover:text-text-muted hover:underline disabled:opacity-50"
        >
          {referring ? '…' : 'awaiting referral'}
        </button>
      )}
      {firmedControl}
      {declineLink}
      <RowMenu market={market} quote={quote} onSubmissionRecord={onSubmissionRecord} onFork={onFork} onClearing={onClearing} onAddOption={onAddOption} />
      {declineDialog}
    </div>
  );
}

export default function MarketsWorkSurface({ dealId, deal, fleetAndRoster, refreshKey, onPresent, onViewIntake }) {
  const { ready: apiReady } = useApiCarrierReadiness();
  const [markets, setMarkets] = useState(null);
  const [quoteByCarrier, setQuoteByCarrier] = useState({});
  const [clearingReferred, setClearingReferred] = useState(false);
  const [offersByCarrier, setOffersByCarrier] = useState({});
  const [aircraft, setAircraft] = useState([]);
  const [docQuoteIds, setDocQuoteIds] = useState(new Set());
  // Drag-drop (Ship 2.3): dragDepth counts enter/leave to survive child bubbling;
  // dragOverId is the highlighted row; pendingFiles holds a dropped PDF per sent
  // row until its quick-log mints the quote it attaches to.
  const [dragging, setDragging] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const [pendingFiles, setPendingFiles] = useState({});
  const [resending, setResending] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [detailQuoteId, setDetailQuoteId] = useState(null); // Ship 3: open Quote detail
  const [recordQuoteId, setRecordQuoteId] = useState(null); // submission record drawer
  const [referringId, setReferringId] = useState(null);
  const dragDepth = useRef(0);

  // Resend the broker-internal "Ready to Quote" summary to the broker's own
  // inbox. This template is (correctly) filtered out of the client ComposeSheet
  // picker, so this is its only UI door. resolveRTQManifest rebuilds fresh + logs.
  const resendRtq = useCallback(async () => {
    setResending(true);
    try {
      const res = await sendTemplateForDeal(dealId, { slug: 'ready-to-quote' });
      // Under EMAIL_OVERRIDE the server answers mode 'override': the mail went
      // to the override address, not res.to, so the toast says so.
      toast.success(res?.mode === 'override'
        ? `Ready to Quote sent to the EMAIL_OVERRIDE address (meant for ${res?.to || 'your inbox'})`
        : `Ready to Quote resent to ${res?.to || 'your inbox'}`);
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Resend failed');
    } finally {
      setResending(false);
    }
  }, [dealId]);

  // Join the deal's quotes onto the rows so a quoted carrier shows its premium
  // + terms (premiums live on quotes, not markets). Lowest premium per carrier.
  //
  // KEYED ON THE RESOLVED CARRIER, NOT ON A DISPLAY NAME. This used to match
  // carriers.name.toLowerCase().trim() against `q.underwriter || q.carrier`,
  // which is three strings any two of which can drift: applyCarrierIdentity
  // rewrites `carrier` to the resolver's canonical form, and `underwriter` is a
  // person's name on plenty of rows. A miss looked identical to the premium
  // having vanished. carrier_ref_id is the Supabase FK on both sides.
  // DEAL-SCOPED, so the repaint does not wait on the Zoho pipeline (prompt 67
  // item 2.2). This read was getQuotesForAccount, whose route awaits
  // cached('tracon_pipeline', …) to map account → deal ids — and quick-log hard-
  // deletes that cache on the way out, so every logged premium blocked its own
  // repaint on a full pipeline rebuild. The rows were always correct and always
  // late, which David read as the feature not working. The account route stays
  // where it belongs, on the account-scoped QuotesPanel.
  const refreshQuotes = useCallback(() => {
    if (!dealId) return;
    getQuotesForDealPanel(dealId)
      .then((r) => {
        let map = {};
        let offers = {};
        for (const q of (r?.quotations || r?.quotes || [])) {
          map = mergeQuoteIntoMap(map, q);
          offers = collectOffers(offers, q);
        }
        setQuoteByCarrier(map);
        setOffersByCarrier(offers);
      })
      .catch(() => {});
  }, [dealId]);

  // THE INSTANT PAINT. quick-log returns the minted panel row; this puts it on
  // the row before the refetch lands. Through the SAME merge rule the refetch
  // uses, so the number that appears on log is the number that survives
  // reconciliation a moment later — a different rule here would show one figure
  // and then quietly swap it for another, which is worse than showing none.
  const applyQuoteRow = useCallback((q) => {
    if (!q) return;
    setQuoteByCarrier((prev) => mergeQuoteIntoMap(prev, q));
  }, []);

  // The one repaint entry point. Every mutation on this surface calls it, so it
  // has to refetch everything a row renders: the market states, the filed docs,
  // AND the premiums. Repainting the rows without the premiums is how a quote
  // logged a second ago rendered as a quoted row carrying no number, which
  // reads as data loss and sent David to the reload button all day.
  const refresh = useCallback(() => {
    if (!dealId) return;
    listDealMarkets(dealId)
      .then((d) => setMarkets(d?.markets || []))
      .catch(() => setMarkets([]));
    getQuoteDocs(dealId)
      .then((r) => setDocQuoteIds(new Set((r?.documents || []).map((d) => d.matched_quote_id).filter(Boolean))))
      .catch(() => {});
    refreshQuotes();
  }, [dealId, refreshQuotes]);

  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  // The deal's tails (with tail_number_id) for the per-aircraft quick-log (A4).
  // Authoritative + deal-scoped, from the same Aircraft_on_Policy the mint reads.
  useEffect(() => {
    if (!dealId) return undefined;
    let dead = false;
    getQuotePrefill(dealId)
      .then((r) => {
        const list = (r?.aircraft || []).filter((a) => a && a.tail_number_id);
        if (!dead) setAircraft(list);
      })
      .catch(() => { if (!dead) setAircraft([]); });
    return () => { dead = true; };
  }, [dealId, refreshKey]);

  // Fact strip one-liner for the ladder header (the primary tail / count).
  const riskLine = useMemo(() => {
    const fleet = fleetAndRoster?.fleet || deal?.aircraft || [];
    if (!fleet.length) return 'this deal';
    const first = fleet[0];
    const tail = first?.tail_number || first?.Tail_Number || first?.n_number || '';
    return fleet.length === 1 ? (tail || '1 aircraft') : `${tail || fleet[0]?.name || ''} +${fleet.length - 1}`.trim();
  }, [fleetAndRoster, deal]);

  // A row accepts a dropped PDF when it is quoted (attach to its quote) or still
  // awaiting an outcome (drop opens quick-log). Declined/blocked do not.
  const isDroppable = (m) => !['declined', 'blocked'].includes(m.state);

  const onPaneDragEnter = useCallback((e) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  }, []);
  const onPaneDragLeave = useCallback(() => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) { setDragging(false); setDragOverId(null); }
  }, []);
  const onPaneDragOver = useCallback((e) => { if (isFileDrag(e)) e.preventDefault(); }, []);
  const onPaneDrop = useCallback((e) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    setDragOverId(null);
  }, []);

  // Mark a submission as awaiting a referral. Writes the QUOTE's referred flag
  // when the row has a quote (the flag with existing readers), and moves the
  // market to 'working' either way, because a referral IS with the carrier and
  // in progress. No new column on submission_markets: the ladder had one string
  // prefix pretending to be a state and a second home would not improve that.
  const markReferred = useCallback(async (market) => {
    setReferringId(market.id);
    try {
      const ids = Array.isArray(market.quote_ids) ? market.quote_ids : [];
      const quoteId = ids.length ? ids[ids.length - 1] : null;
      if (quoteId) await patchQuote(quoteId, { referred: true });
      // A NO-OP STATE CHANGE IS STILL AN EVENT (187 addendum §2). The state
      // move stays conditional — moving `working` to `working` is a redundant
      // WRITE — but the referral is recorded either way. Market 334 was already
      // `working` because Global's API put it there the same second, so this
      // guard silently swallowed the fact as well as the write, and the row's
      // whole history reads sent → working with the word "referred" nowhere in
      // it. For Global that referral is the only appetite signal on file.
      if (market.state !== 'working') await setMarketState(market.id, { state: 'working' });
      await recordMarketReferral(market.id, {});
      toast.success(`${market.carrier_name || 'Carrier'} marked awaiting referral`);
      refresh();
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Could not mark it referred');
    } finally {
      setReferringId(null);
    }
  }, [refresh]);

  // The two clearing controls (187 §5). Attributed server-side; the basis
  // travels because "the carrier firmed it" and "it was never referred" are
  // different claims about a carrier and must not share a spelling.
  const clearReferred = useCallback(async (quoteId, basis) => {
    if (!quoteId) { toast.error('No quote on this row to clear'); return; }
    setClearingReferred(true);
    try {
      await clearQuoteReferral(quoteId, basis);
      toast.success(basis === 'firmed' ? 'Terms confirmed, referral cleared' : 'Referral cleared');
      refresh();
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Could not clear the referral');
    } finally {
      setClearingReferred(false);
    }
  }, [refresh]);

  // Fork lands him straight in the wizard on the NEW row, so the next thing he
  // does is change the one thing that differs. That is the whole point of the
  // item: clone and change, never re-key.
  const handleFork = useCallback(async (quoteId) => {
    try {
      const r = await forkQuote(quoteId);
      toast.success('Forked. Change what differs.');
      refresh();
      if (r?.quote_id) setDetailQuoteId(r.quote_id);
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Fork failed');
    }
  }, [refresh]);

  const onDropRow = useCallback(async (market, e) => {
    dragDepth.current = 0;
    setDragging(false);
    setDragOverId(null);
    const file = firstDroppedPdf(e);
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Only PDFs attach to a market row'); return; }

    if (market.state === 'quoted') {
      const quoteId = quoteIdFor(market, market.carrier_ref_id != null ? quoteByCarrier[String(market.carrier_ref_id)] : null);
      // A QUOTED ROW WITH NO QUOTATION STAGES THE FILE instead of refusing it
      // (prompt 96 item 1). "This quoted row has no quotation to attach to" was
      // the third dead end on the same row: it named the missing thing and left
      // the PDF on the floor. The quick-log now renders on that row, so the file
      // waits for it exactly as it does on a sent row.
      if (!quoteId) {
        setPendingFiles((m) => ({ ...m, [market.id]: file }));
        toast.success('Log the premium to file this quote');
        return;
      }
      try {
        await attachQuoteSourcePdf(dealId, quoteId, file);
        toast.success('Quote PDF attached');
        refresh();
      } catch (err) {
        toast.error(err.message || 'Attach failed');
      }
      return;
    }
    // Awaiting an outcome: a dropped quote means one arrived — open quick-log
    // with the file staged; it attaches when the broker logs the premium.
    setPendingFiles((m) => ({ ...m, [market.id]: file }));
    toast.success('Log the premium to file this quote');
  }, [dealId, refresh, quoteByCarrier]);

  // THE REPAIR FOR A ROW THAT SAYS QUOTED AND HOLDS NOTHING. It moves the
  // market's own state back and writes nothing else: it is not a decline, and it
  // makes no claim about what the carrier said. The other door out of that row
  // is the quick-log now rendered on it; this one is for the case where the
  // answer never actually came.
  const markClearing = useCallback(async (market, clearing) => {
    try {
      await setMarketClearing(market.id, clearing);
      toast.success(clearing
        ? `${market.carrier_name || 'Market'} recorded as a clearance`
        : `${market.carrier_name || 'Market'} recorded as a submission`);
      refresh();
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Could not correct it');
    }
  }, [refresh]);

  const unquote = useCallback(async (market) => {
    try {
      await setMarketState(market.id, { state: 'sent' });
      toast.success(`${market.carrier_name || 'Market'} moved back to sent`);
      refresh();
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Could not move it back');
    }
  }, [refresh]);

  // Does this carrier already have a rung? Decides whether its fire button
  // sits in the header (no row yet) or on the row itself.
  const hasMarketFor = (slug) => (markets || []).some((m) => m.carrier_slug === slug);
  // Offers available to compare. Keyed on the market state, not on the quote
  // join, so the button appears exactly when there is something to compare.
  const quotedMarketCount = (markets || []).filter((m) => m.state === 'quoted').length;

  if (!markets) return null;

  return (
    <div
      className="rounded-card border border-border bg-surface px-3.5 py-3"
      onDragEnter={onPaneDragEnter}
      onDragLeave={onPaneDragLeave}
      onDragOver={onPaneDragOver}
      onDrop={onPaneDrop}>
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-dim">
          Markets{riskLine ? `: ${riskLine}` : ''}
        </p>
        <div className="flex items-center gap-1.5">
          {/* Live-API doors. Everything else in this header logs something the
              broker already did in a portal; these actually fire a quote.
              PLACEMENT RULE (prompt 79 items 3+4): an API fire button belongs
              to ONE carrier, never to "the ladder". So it lives wherever that
              carrier currently is — here while the carrier has no row yet
              (firing is what creates the row), and on the carrier's own row
              once it does. Never both, and the label always names the carrier.
              Hidden entirely unless the integration reports environment=prod,
              so a UAT-pointed door does not masquerade as a live one. */}
          {apiReady.global_aerospace && !hasMarketFor('global_aerospace') ? (
            <GlobalQuoteAction dealId={dealId} onLanded={refresh} />
          ) : null}
          {apiReady.iat && !hasMarketFor('iat') ? (
            <IatQuoteAction dealId={dealId} onLanded={refresh} />
          ) : null}
          {onViewIntake ? (
            <button
              type="button"
              onClick={onViewIntake}
              className="rounded-pill border border-border px-2.5 py-0.5 text-[11px] text-text-muted hover:border-border-strong hover:text-text"
            >
              View submission intake
            </button>
          ) : null}
          {/* Quote vs quote, field by field. Only offered once there are two
              offers to compare — a compare button over one quote is a control
              that does nothing. No ranking: the differences are the point. */}
          {quotedMarketCount > 1 ? (
            <button
              type="button"
              onClick={() => setCompareOpen(true)}
              className="rounded-pill border border-border px-2.5 py-0.5 text-[11px] text-text-muted hover:border-border-strong hover:text-text"
              title="Compare these quotes against each other, field by field"
            >
              Compare quotes
            </button>
          ) : null}
          <button
            type="button"
            onClick={resendRtq}
            disabled={resending}
            className="rounded-pill border border-border px-2.5 py-0.5 text-[11px] text-text-muted hover:border-border-strong hover:text-text disabled:opacity-50"
            title="Resend the Ready to Quote summary to your own inbox"
          >
            {resending ? 'Sending…' : 'Resend to my inbox'}
          </button>
          {/* "+ Log a submission" REMOVED (prompt 104 C4). David: "I still have
              'Log a submission'. Why? Just for a third door to the same
              hallway?" The phantom row replaced it. The one thing the dialog
              collected that the row does not is a free-text NOTE (blurb) — that
              is a gap in the row, not a reason to keep a second door, and blurb
              is still written by the dispatch and Global paths so nothing that
              renders it goes blank. */}
        </div>
      </div>

      {markets.length === 0 ? (
        <p className="py-1 text-[11.5px] text-text-muted">
          No markets yet. Type one on the row below.
        </p>
      ) : (
        markets.map((m) => (
          <MarketWorkRow
            key={m.id}
            dealId={dealId}
            apiReady={apiReady}
            market={m}
            quote={m.carrier_ref_id != null ? quoteByCarrier[String(m.carrier_ref_id)] : undefined}
            aircraft={aircraft}
            hasDoc={[
              ...(Array.isArray(m.quote_ids) ? m.quote_ids : []),
              m.carrier_ref_id != null ? quoteByCarrier[String(m.carrier_ref_id)]?.id : null,
            ].filter(Boolean).some((qid) => docQuoteIds.has(qid))}
            pendingFile={pendingFiles[m.id]}
            dragging={dragging}
            dragOver={dragOverId === m.id}
            droppable={isDroppable(m)}
            onDragOverRow={setDragOverId}
            onDragLeaveRow={(id) => setDragOverId((cur) => (cur === id ? null : cur))}
            onDropRow={onDropRow}
            onPresent={onPresent}
            onSubmissionRecord={setRecordQuoteId}
            onMarkReferred={markReferred}
            onFork={handleFork}
            onQuoteLogged={applyQuoteRow}
            onUnquote={unquote}
            onClearReferred={clearReferred}
            clearingReferred={clearingReferred}
            onClearing={markClearing}
            referring={referringId === m.id}
            offers={m.carrier_ref_id != null ? offersByCarrier[String(m.carrier_ref_id)] : undefined}
            onAddOption={(qid) => setDetailQuoteId(qid)}
            onQuoteDetail={(mkt, explicitQuoteId) => {
              // An offer row names its own quote. Without this the expanded
              // list would open the carrier's LOWEST quote whichever line was
              // clicked, which is the same false statement one layer down.
              if (explicitQuoteId) { setDetailQuoteId(explicitQuoteId); return; }
              // quote_ids first (this round's own capture), then the
              // carrier-matched quote — the row renders that one's premium, so
              // refusing to open it read as "no quotation" on a row visibly
              // showing a number.
              const qid = quoteIdFor(mkt, mkt.carrier_ref_id != null ? quoteByCarrier[String(mkt.carrier_ref_id)] : null);
              // NO ELSE-TOAST. The button is not rendered without a quotation
              // any more, so there is nothing left to refuse; the row shows the
              // quick-log instead. A toast that named a missing thing and
              // offered nothing was the whole complaint.
              if (qid) setDetailQuoteId(qid);
            }}
            onPendingConsumed={() => setPendingFiles((mp) => { const n = { ...mp }; delete n[m.id]; return n; })}
            onChanged={refresh}
          />
        ))
      )}

      {/* THE PHANTOM ROW (prompt 93). Always present, at the bottom of the
          ladder, so logging a market is a name and a tab rather than a modal.
          The header's "+ Log a submission" stays as the long-form door: it is
          the only one that collects a free-text note. */}
      <PhantomMarketRow
        dealId={dealId}
        aircraft={aircraft}
        markets={markets}
        onLogged={refresh}
      />



      {/* QUOTE-WIZARD-SPEC phase 1. The stepped surface for a quick-logged quote
          the broker is actually going to shop. Quick-log above stays exactly as
          fast; this is the depth, and it is opened per quote, on demand. */}
      <QuoteWizardSheet
        dealId={dealId}
        quoteId={detailQuoteId}
        open={!!detailQuoteId}
        onOpenChange={(o) => { if (!o) setDetailQuoteId(null); }}
        onSaved={refresh}
      />

      {/* What we submitted, what came back, and the docs it generated.
          Broker-only: the submitted payload carries pilot PII. */}
      <SubmissionRecordDrawer
        quoteId={recordQuoteId}
        open={!!recordQuoteId}
        onOpenChange={(o) => { if (!o) setRecordQuoteId(null); }}
      />

      {/* Quote vs quote. Reads the existing present-options payload, which
          already carries the full option grain including both deductibles. */}
      <CompareQuotesSheet dealId={dealId} open={compareOpen} onOpenChange={setCompareOpen} />
    </div>
  );
}
